"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ShieldCheck, TrendingUp, Users, ChartBar as BarChart3 } from 'lucide-react';
export default function Agentbenefits() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const pillar1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillar2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillar3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillar4 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "Final Expense Focus",
    description: "Access a nationwide base of qualified customers ready for Final Expense coverage. We connect you with high-intent individuals seeking immediate protection for their families.",
    icon: ShieldCheck,
    ref: pillar1.ref,
    isVisible: pillar1.isVisible,
    delay: 0,
    offsetClass: "md:mt-0"
  }, {
    number: "02",
    title: "Performance-Based",
    description: "We operate on a results-first model. By aligning our success with yours, we ensure that our lead generation and appointment setting efforts directly contribute to your bottom line with zero upfront risk.",
    icon: TrendingUp,
    ref: pillar2.ref,
    isVisible: pillar2.isVisible,
    delay: 150,
    offsetClass: "md:mt-8 lg:mt-12"
  }, {
    number: "03",
    title: "Qualified Outreach",
    description: "Our team specializes in high-intent customer acquisition. We filter and verify every lead, ensuring that your sales team spends their time closing deals rather than chasing cold prospects.",
    icon: Users,
    ref: pillar3.ref,
    isVisible: pillar3.isVisible,
    delay: 300,
    offsetClass: "md:mt-0"
  }, {
    number: "04",
    title: "Scalable Growth",
    description: "Whether you are an independent agent or a growing agency, our infrastructure scales with you. We provide the consistent pipeline of appointments needed to dominate your market and increase commissions.",
    icon: BarChart3,
    ref: pillar4.ref,
    isVisible: pillar4.isVisible,
    delay: 450,
    offsetClass: "md:mt-8 lg:mt-12"
  }];
  return <section data-section-id="3189" id="agentbenefits" className="bg-background text-foreground py-20 md:py-28 lg:py-32 px-6 sm:px-8 lg:px-12 relative overflow-hidden">
      {/* Side tracker for large viewports */}
      <div className="hidden xl:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-3 font-mono text-xs tracking-[0.25em] text-primary bg-card/90 backdrop-blur-md py-6 px-3.5 border border-border/80 rounded-xl shadow-md z-10">
        <span className={pillar1.isVisible ? "text-accent font-bold scale-110 transition-transform" : "opacity-40"}>01</span>
        <span className="opacity-30 text-[10px]">↓</span>
        <span className={pillar2.isVisible ? "text-accent font-bold scale-110 transition-transform" : "opacity-40"}>02</span>
        <span className="opacity-30 text-[10px]">↓</span>
        <span className={pillar3.isVisible ? "text-accent font-bold scale-110 transition-transform" : "opacity-40"}>03</span>
        <span className="opacity-30 text-[10px]">↓</span>
        <span className={pillar4.isVisible ? "text-accent font-bold scale-110 transition-transform" : "opacity-40"}>04</span>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Eyebrow */}
        <div ref={eyebrow.ref} className={`font-mono text-xs md:text-sm font-bold tracking-[0.2em] mb-4 transition-all duration-700 ease-out text-primary ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          — 02 / OUR ADVANTAGE
        </div>

        {/* Heading */}
        <h2 ref={heading.ref} className={`font-default text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.12] max-w-3xl mb-16 md:mb-24 transition-all duration-1000 ease-out text-foreground ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Empowering agents with{" "}
          <em className="italic font-light text-primary">
            proven results
          </em>
          .
        </h2>

        {/* Benefits Grid */}
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10 pb-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={pillar.number} data-index={i} ref={pillar.ref} className={`${pillar.offsetClass} bg-card text-card-foreground border border-border/80 rounded-xl p-8 sm:p-10 shadow-sm hover:shadow-md transition-all duration-500 ease-out hover:border-accent/60 relative flex flex-col justify-between group ${pillar.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${pillar.delay}ms`
          }}>
                <div>
                  {/* Card Header: Icon + Metric Number */}
                  <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-border/70">
                    <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 group-hover:border-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-300 shadow-sm">
                      <Icon className="size-5" />
                    </div>
                    <span className="font-mono text-3xl sm:text-4xl font-light tracking-tight text-muted-foreground/60 group-hover:text-accent transition-colors duration-300">
                      {pillar.number}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-default font-bold text-xl sm:text-2xl tracking-tight leading-snug mb-3 text-card-foreground group-hover:text-primary transition-colors duration-300">
                    {pillar.title}
                  </h3>

                  {/* Description */}
                  <p className="font-default text-sm sm:text-base leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </p>
                </div>

                {/* Refined bottom accent indicator */}
                <div className="pt-8">
                  <div className="h-1 w-10 bg-primary/20 group-hover:w-16 group-hover:bg-accent transition-all duration-300 rounded-lg" />
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}